import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../i18n';
import { trackEvent } from '../analytics';

const round50 = (value) => Math.max(0, Math.round(value / 50) * 50);

const copy = {
  fr: {
    kicker: 'Estimation guidée · 3 à 5 minutes',
    title: 'Voyons ce que votre projet doit vraiment faire.',
    intro: 'Pas de jargon technique. Vous choisissez ce qui serait utile à votre activité, je traduis cela en périmètre, délai et ordre de grandeur.',
    progress: 'Progression',
    next: 'Continuer',
    back: 'Retour',
    reset: 'Recommencer',
    recommended: 'Idée à considérer',
    impact: { low: 'impact léger', mid: 'impact moyen', high: 'impact important' },
    scopeLabels: { starter: 'Première version ciblée', complete: 'Projet plus complet' },
    customBudget: 'Sur devis, après examen du périmètre',
    complexReason: 'Ce besoin combine plusieurs modules métier. Une fourchette automatique serait trompeuse : je préfère examiner l’organisation des données, les rôles, les intégrations et la reprise éventuelle avant de vous donner un prix.',
    starterReason: 'Ce chiffre concerne une première version avec les fonctions choisies. Il ne correspond pas au coût de reproduction d’une plateforme métier complète.',
    completeReason: 'Le budget inclut une marge de complexité pour l’ensemble du projet. Il reste indicatif : les règles métier et les intégrations devront être cadrées pour établir un devis.',
    resultEyebrow: 'Votre première version',
    resultTitle: 'Une estimation assez précise pour décider quoi faire ensuite.',
    budget: 'Budget indicatif',
    timing: 'Délai probable',
    format: 'Format conseillé',
    included: 'Ce que cette estimation comprend',
    later: 'Ce qui peut attendre',
    note: 'Ce résultat ne remplace pas un devis. Il donne un ordre de grandeur à partir du périmètre sélectionné.',
    contact: 'Parler de cette estimation',
    copySummary: 'Copier le résumé',
    copied: 'Résumé copié',
    formatLabels: {
      present: 'Site vitrine',
      leads: 'Site orienté demandes / devis',
      booking: 'Site avec réservation',
      sell: 'E-commerce / paiement',
      manage: 'Outil métier / espace de gestion',
      finish: 'Reprise de projet existant',
    },
  },
  en: {
    kicker: 'Guided estimate · 3 to 5 minutes',
    title: 'Let’s work out what your project actually needs to do.',
    intro: 'No technical jargon. Pick what would help your business and I translate it into scope, timing and a realistic budget range.',
    progress: 'Progress',
    next: 'Continue',
    back: 'Back',
    reset: 'Start over',
    recommended: 'Worth considering',
    impact: { low: 'light impact', mid: 'medium impact', high: 'significant impact' },
    scopeLabels: { starter: 'Focused first version', complete: 'More complete project' },
    customBudget: 'Custom quote after a project review',
    complexReason: 'Several interconnected modules are involved. An automatic price would be misleading: I need to review data, roles, integrations and existing systems before quoting.',
    starterReason: 'This range describes an initial version with selected features, not the cost of reproducing a complete business platform.',
    completeReason: 'The range takes a broader implementation into account. Business rules and integrations still require a proper quote.',
    resultEyebrow: 'Your first version',
    resultTitle: 'An estimate precise enough to decide what happens next.',
    budget: 'Indicative budget',
    timing: 'Likely timing',
    format: 'Recommended format',
    included: 'Included in this estimate',
    later: 'Can wait until later',
    note: 'This is not an automatic quote. It gives a range based on the selected scope.',
    contact: 'Discuss this estimate',
    copySummary: 'Copy summary',
    copied: 'Summary copied',
    formatLabels: {
      present: 'Business website',
      leads: 'Lead-focused website',
      booking: 'Booking website',
      sell: 'E-commerce / payments',
      manage: 'Business tool / admin',
      finish: 'Existing project takeover',
    },
  },
  pt: {
    kicker: 'Estimativa guiada · 3 a 5 minutos',
    title: 'Vamos entender o que o seu projeto realmente precisa fazer.',
    intro: 'Sem jargão técnico. Você escolhe o que seria útil e eu traduzo em escopo, prazo e faixa de orçamento.',
    progress: 'Progresso',
    next: 'Continuar',
    back: 'Voltar',
    reset: 'Recomeçar',
    recommended: 'Vale considerar',
    impact: { low: 'impacto leve', mid: 'impacto médio', high: 'impacto importante' },
    scopeLabels: { starter: 'Primeira versão focada', complete: 'Projeto mais completo' },
    customBudget: 'Orçamento após análise do projeto',
    complexReason: 'O projeto reúne diversos módulos. Um preço automático seria enganador: preciso analisar dados, perfis e integrações para fazer um orçamento.',
    starterReason: 'Esta faixa refere-se a uma primeira versão com funções selecionadas, não ao custo de uma plataforma completa.',
    completeReason: 'A faixa inclui uma margem para um projeto mais completo. As regras de negócio e integrações devem ser analisadas.',
    resultEyebrow: 'Sua primeira versão',
    resultTitle: 'Uma estimativa suficientemente precisa para decidir o próximo passo.',
    budget: 'Orçamento indicativo',
    timing: 'Prazo provável',
    format: 'Formato recomendado',
    included: 'Incluído nesta estimativa',
    later: 'Pode ficar para depois',
    note: 'Isto não é um orçamento automático. É uma faixa baseada no escopo selecionado.',
    contact: 'Falar sobre esta estimativa',
    copySummary: 'Copiar resumo',
    copied: 'Resumo copiado',
    formatLabels: {
      present: 'Site institucional',
      leads: 'Site para pedidos / orçamentos',
      booking: 'Site com reservas',
      sell: 'E-commerce / pagamentos',
      manage: 'Ferramenta de gestão',
      finish: 'Retomada de projeto existente',
    },
  },
};

const steps = {
  start: {
    q: 'Où en êtes-vous aujourd’hui ?',
    help: 'Cela change surtout la quantité de travail à reprendre.',
    options: [
      { id: 'new', label: 'Je pars de zéro', desc: 'Idée, activité ou besoin, mais rien de construit.' },
      { id: 'website', label: 'J’ai déjà un site', desc: 'Le site existe, mais il faut le faire évoluer.' },
      { id: 'unfinished', label: 'Un projet est commencé', desc: 'IA, no-code ou autre développeur : il faut terminer proprement.' },
    ],
  },
  goal: {
    q: 'Quel résultat compte le plus pour vous ?',
    help: 'Choisissez le bénéfice principal. On ajoutera les fonctions ensuite.',
    options: [
      { id: 'present', label: 'Présenter mon activité', desc: 'Être crédible, clair et facile à contacter.' },
      { id: 'leads', label: 'Recevoir de meilleures demandes', desc: 'Qualifier un prospect, un devis ou une prise de contact.' },
      { id: 'booking', label: 'Prendre des réservations', desc: 'Créneaux, disponibilités et confirmations.' },
      { id: 'sell', label: 'Vendre ou encaisser', desc: 'Produits, prestations, acompte ou paiement complet.' },
      { id: 'manage', label: 'Gérer mon activité', desc: 'Clients, stock, commandes, documents ou suivi interne.' },
      { id: 'finish', label: 'Finir / réparer l’existant', desc: 'Débloquer un projet et le rendre exploitable.' },
    ],
  },
  features: {
    q: 'Qu’est-ce qui pourrait vous faire gagner du temps ?',
    help: 'Sélectionnez plusieurs idées. Les suggestions ne sont pas des obligations.',
    multi: true,
    options: [
      { id: 'booking', label: 'Réservation en ligne', desc: 'Le client choisit lui-même un créneau disponible.', impact: 'mid', recommendFor: ['booking', 'present', 'leads'] },
      { id: 'payment', label: 'Paiement en ligne', desc: 'Encaisser une prestation ou une commande directement.', impact: 'mid', recommendFor: ['sell', 'booking'] },
      { id: 'deposit', label: 'Acompte', desc: 'Sécuriser une réservation sans demander le paiement complet.', impact: 'low', recommendFor: ['booking'] },
      { id: 'emails', label: 'Emails automatiques', desc: 'Confirmation, rappel ou suivi sans intervention manuelle.', impact: 'low', recommendFor: ['booking', 'sell', 'leads'] },
      { id: 'quote', label: 'Demande de devis guidée', desc: 'Collecter les bonnes informations avant de répondre.', impact: 'low', recommendFor: ['leads', 'present'] },
      { id: 'shop', label: 'Catalogue / boutique', desc: 'Présenter et vendre plusieurs produits ou prestations.', impact: 'high', recommendFor: ['sell'] },
      { id: 'admin', label: 'Espace de gestion', desc: 'Gérer contenus, réservations, commandes ou clients au même endroit.', impact: 'mid', recommendFor: ['manage', 'booking', 'sell'] },
      { id: 'accounts', label: 'Compte client', desc: 'Le client retrouve ses informations ou son historique.', impact: 'high', recommendFor: ['manage', 'sell'] },
      { id: 'dashboard', label: 'Tableau de bord', desc: 'Voir les informations importantes sans fouiller dans plusieurs outils.', impact: 'mid', recommendFor: ['manage'] },
    ],
  },
  connections: {
    q: 'Votre projet doit-il parler à autre chose ?',
    help: 'C’est souvent ici que deux projets qui se ressemblent ont des coûts très différents.',
    multi: true,
    options: [
      { id: 'none', label: 'Non / je ne sais pas', desc: 'On part sur un périmètre autonome et simple.' },
      { id: 'external', label: 'Un logiciel déjà utilisé', desc: 'CRM, agenda, outil métier ou autre service à connecter.', impact: 'high' },
      { id: 'import', label: 'Importer des données', desc: 'Excel, CSV ou données venant d’un ancien système.', impact: 'mid' },
      { id: 'export', label: 'Générer des PDF / exports', desc: 'Factures, devis, rapports, CSV ou documents.', impact: 'mid' },
    ],
  },
  scope: {
    q: 'Quelle ampleur imaginez-vous pour ce projet ?',
    help: 'Un site avec quelques fonctions et une plateforme complète ne demandent pas le même travail.',
    options: [
      { id: 'starter', label: 'Une première version ciblée', desc: 'Les fonctions essentielles pour lancer rapidement et faire évoluer ensuite.' },
      { id: 'complete', label: 'Un projet complet', desc: 'Plusieurs parcours, davantage de gestion et des fonctions reliées entre elles.' },
      { id: 'unsure', label: 'Je ne sais pas encore', desc: 'Estimez une première étape, puis discutons de la suite.' },
    ],
  },
  timing: {
    q: 'Quand aimeriez-vous pouvoir l’utiliser ?',
    help: 'Le délai idéal permet de choisir une première version réaliste.',
    options: [
      { id: 'urgent', label: 'Très vite', desc: 'Moins de deux semaines : il faudra prioriser.' },
      { id: 'month', label: 'Dans le mois', desc: 'Bon rythme pour une première version bien cadrée.' },
      { id: 'flexible', label: 'Je suis flexible', desc: 'On privilégie le bon périmètre au calendrier.' },
    ],
  },
};

const scopeTranslations = {
  en: {
    q: 'How much do you want this first project to cover?',
    help: 'A small launch and a platform with connected workflows need very different budgets.',
    options: [
      { id: 'starter', label: 'A focused first release', desc: 'The essentials to launch and grow later.' },
      { id: 'complete', label: 'A complete project', desc: 'Several customer flows, admin features and integrations.' },
      { id: 'unsure', label: 'Not sure yet', desc: 'Estimate a first step, then we can plan the rest.' },
    ],
  },
  pt: {
    q: 'Qual dimensão imagina para o projeto?',
    help: 'Uma primeira versão e uma plataforma integrada têm custos diferentes.',
    options: [
      { id: 'starter', label: 'Uma primeira versão', desc: 'Funções essenciais para começar e evoluir depois.' },
      { id: 'complete', label: 'Um projeto completo', desc: 'Vários percursos, gestão e integrações.' },
      { id: 'unsure', label: 'Ainda não sei', desc: 'Estime uma primeira fase e depois ajustamos.' },
    ],
  },
};
const sequence = ['start', 'goal', 'features', 'connections', 'scope', 'timing'];
const base = {
  present: [850, 1250],
  leads: [950, 1450],
  booking: [1250, 1750],
  sell: [1500, 2200],
  manage: [1550, 2500],
  finish: [350, 850],
};
// Scope is independent from feature add-ons. A full platform is not a starter template
// with the same price tag: integration, back-office and acceptance take more work.
const fullScopeAdders = {
  present: [800, 1500],
  leads: [1050, 1800],
  booking: [1400, 2700],
  sell: [2200, 3800],
  manage: [3400, 6400],
  finish: [700, 1900],
};
const adders = {
  booking: [250, 450],
  payment: [250, 450],
  deposit: [100, 220],
  emails: [100, 220],
  quote: [150, 300],
  shop: [550, 950],
  admin: [300, 550],
  accounts: [450, 800],
  dashboard: [350, 650],
  external: [350, 800],
  import: [250, 500],
  export: [200, 450],
};
const core = {
  booking: new Set(['booking', 'emails']),
  sell: new Set(['payment']),
  manage: new Set(['admin']),
  finish: new Set([]),
  present: new Set([]),
  leads: new Set([]),
};

function analyse(answers, t) {
  const goal = answers.goal || 'present';
  let values = base[goal] || base.present;
  let min = values[0];
  let max = values[1];
  let complexity = 1;
  const selected = [...(answers.features || []), ...(answers.connections || [])].filter((id) => id !== 'none');
  const builtIn = core[goal] || new Set();

  selected.forEach((id) => {
    if (builtIn.has(id) || !adders[id]) return;
    min += adders[id][0];
    max += adders[id][1];
    complexity += adders[id][1] >= 700 ? 2 : 1;
  });

  const isComplete = answers.scope === 'complete';
  if (isComplete) {
    const more = fullScopeAdders[goal] || fullScopeAdders.present;
    min += more[0];
    max += more[1];
    complexity += 3;
  }

  if (answers.start === 'website') {
    min += 150;
    max += 350;
    complexity += 1;
  }
  if (answers.start === 'unfinished' && goal !== 'finish') {
    min += 250;
    max += 600;
    complexity += 2;
  }
  if (answers.start === 'unfinished' && goal === 'finish') max += selected.length * 120;

  const labels = {};
  steps.features.options.concat(steps.connections.options).forEach((item) => { labels[item.id] = item.label; });
  const included = [];
  if (goal === 'present') included.push('Pages essentielles', 'Responsive mobile', 'Formulaire de contact', 'Mise en ligne');
  if (goal === 'leads') included.push('Pages essentielles', 'Parcours de demande', 'Formulaire qualifié', 'Mise en ligne');
  if (goal === 'booking') included.push('Site complet', 'Réservation', 'Disponibilités', 'Confirmation');
  if (goal === 'sell') included.push('Offre ou catalogue simple', 'Paiement', 'Suivi des commandes', 'Mise en ligne');
  if (goal === 'manage') included.push('Interface de gestion', 'Base de données', 'Logique métier', 'Mise en ligne');
  if (goal === 'finish') included.push('Audit rapide de l’existant', 'Priorités', 'Corrections ciblées', 'Mise en ligne si nécessaire');
  selected.forEach((id) => {
    if (labels[id] && !included.includes(labels[id])) included.push(labels[id]);
  });

  const later = [];
  if (selected.includes('accounts')) later.push('Le compte client peut souvent attendre une V2 si le budget de départ doit rester contenu.');
  if (selected.includes('dashboard') && goal !== 'manage') later.push('Le tableau de bord peut être simplifié au lancement puis enrichi avec de vraies données.');
  if (selected.includes('shop') && goal !== 'sell') later.push('La boutique peut être séparée du lancement si l’objectif principal est d’abord la visibilité ou la réservation.');
  if (!isComplete) later.push('Une plateforme complète avec plusieurs modules métier devra faire l’objet d’un devis distinct.');
  else later.push('Les règles métier et les intégrations restent à vérifier avant un devis ferme.');
  if (!later.length) later.push('Le périmètre sélectionné peut former une première version cohérente.');

  // For a full multi-module business platform, no automatic quote is credible.
  const customQuote = isComplete && (
    (goal === 'manage' && (selected.length >= 4 || (selected.includes('external') && selected.includes('accounts'))))
    || (goal === 'sell' && selected.length >= 7)
    || (goal === 'booking' && selected.length >= 7)
  );

  let delay = '1 à 2 semaines';
  if (customQuote) delay = 'Après cadrage du projet';
  else if (goal === 'finish' && complexity <= 2) delay = '2 à 7 jours';
  else if (complexity >= 7) delay = '3 à 6 semaines';
  else if (complexity >= 4) delay = '2 à 4 semaines';
  if (answers.timing === 'urgent' && complexity >= 4) later.unshift('Pour tenir un délai très court, il faudra retirer au moins une fonction non essentielle de la première version.');

  return {
    goal,
    scope: isComplete ? 'complete' : 'starter',
    customQuote,
    min: round50(min),
    max: round50(max),
    delay,
    included,
    later,
    label: t.formatLabels[goal] || t.formatLabels.present,
  };
}

export default function SmartEstimator({ embedded = false }) {
  const { lang } = useLang();
  const navigate = useNavigate();
  const t = copy[lang] || copy.fr;
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const goal = params.get('goal');
    if (goal && base[goal]) {
      setAnswers((prev) => ({ ...prev, goal }));
      setIndex(2);
    }
    trackEvent('estimator_view', { embedded });
  }, [embedded]);

  const done = index >= sequence.length;
  const stepId = sequence[index];
  const step = stepId === 'scope' && scopeTranslations[lang] ? scopeTranslations[lang] : steps[stepId];
  const result = useMemo(() => analyse(answers, t), [answers, t]);

  const pick = (id) => {
    if (step.multi) {
      setAnswers((prev) => {
        const old = prev[stepId] || [];
        const clean = id === 'none' ? [] : old.filter((item) => item !== 'none');
        const next = clean.includes(id) ? clean.filter((item) => item !== id) : [...clean, id];
        return { ...prev, [stepId]: id === 'none' ? ['none'] : next };
      });
      return;
    }
    setAnswers((prev) => ({ ...prev, [stepId]: id }));
    trackEvent('estimator_step_completed', { step: stepId, choice: id });
    setIndex((value) => value + 1);
  };

  const canContinue = step && step.multi && (answers[stepId] || []).length > 0;

  const summary = useMemo(() => {
    if (!done) return '';
    const lines = [
      'Projet : ' + result.label,
      'Périmètre : ' + t.scopeLabels[result.scope],
      'Budget indicatif : ' + (result.customQuote ? t.customBudget : result.min.toLocaleString('fr-FR') + ' € à ' + result.max.toLocaleString('fr-FR') + ' €'),
      'Délai probable : ' + result.delay,
      '',
      'Fonctions / périmètre :',
    ];
    result.included.forEach((item) => lines.push('- ' + item));
    lines.push('', 'Notes :');
    result.later.forEach((item) => lines.push('- ' + item));
    return lines.join('\n');
  }, [done, result, t]);

  useEffect(() => {
    if (done) trackEvent('estimator_completed', { goal: result.goal, scope: result.scope, quote_mode: result.customQuote ? 'custom' : 'range', ...(result.customQuote ? {} : { estimate_min: result.min, estimate_max: result.max }) });
  }, [done, result.goal, result.scope, result.customQuote, result.min, result.max]);

  const contact = () => {
    sessionStorage.setItem('prefilledContactMessage', 'Bonjour Alioune,\n\nJ’ai utilisé l’estimateur :\n\n' + summary + '\n\nVoici quelques précisions sur mon projet :\n');
    trackEvent('estimator_contact_click', { goal: result.goal, scope: result.scope, quote_mode: result.customQuote ? 'custom' : 'range' });
    navigate('/#contact');
  };

  const copyResult = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const resetAll = () => {
    setAnswers({});
    setIndex(0);
    trackEvent('estimator_reset');
  };

  return (
    <section className={'smart-estimator' + (embedded ? ' embedded' : '')}>
      {!embedded && (
        <div className="estimator-intro">
          <span className="v2-kicker">{t.kicker}</span>
          <h1>{t.title}</h1>
          <p>{t.intro}</p>
        </div>
      )}

      <div className="estimator-shell">
        <div className="estimator-progress">
          <div className="estimator-progress-meta">
            <span>{t.progress}</span>
            <strong>{done ? 100 : Math.round((index / sequence.length) * 100)}%</strong>
          </div>
          <div className="estimator-track"><motion.i animate={{ width: (done ? 100 : (index / sequence.length) * 100) + '%' }} /></div>
        </div>

        <AnimatePresence mode="wait">
          {!done ? (
            <motion.div className="estimator-step" key={stepId} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}>
              <div className="estimator-step-head">
                <span>0{index + 1}</span>
                <div><h2>{step.q}</h2><p>{step.help}</p></div>
              </div>

              <div className={'estimator-options' + (step.multi ? ' multi' : '')}>
                {step.options.map((option) => {
                  const selected = step.multi ? (answers[stepId] || []).includes(option.id) : answers[stepId] === option.id;
                  const recommended = option.recommendFor && option.recommendFor.includes(answers.goal);
                  return (
                    <button type="button" key={option.id} className={'estimator-option' + (selected ? ' selected' : '')} onClick={() => pick(option.id)}>
                      <span className="estimator-check">{selected ? '✓' : '+'}</span>
                      <span className="estimator-option-copy">
                        <strong>{option.label}</strong>
                        <small>{option.desc}</small>
                        <span className="option-meta">
                          {recommended && <em>{t.recommended}</em>}
                          {option.impact && <b>{t.impact[option.impact]}</b>}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="estimator-actions">
                <button type="button" className="v2-link-button" disabled={index === 0} onClick={() => setIndex((value) => Math.max(0, value - 1))}>← {t.back}</button>
                {step.multi && (
                  <button type="button" className="v2-primary" disabled={!canContinue} onClick={() => {
                    trackEvent('estimator_step_completed', { step: stepId, choices: (answers[stepId] || []).join(',') });
                    setIndex((value) => value + 1);
                  }}>{t.next} →</button>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div className="estimator-result" key="result" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}>
              <div className="result-top">
                <div><span className="v2-kicker">{t.resultEyebrow}</span><h2>{t.resultTitle}</h2></div>
                <button type="button" className="v2-link-button" onClick={resetAll}>{t.reset}</button>
              </div>

              <div className="result-dashboard">
                <article className="result-main">
                  <span>{t.format}</span>
                  <strong>{result.label} · {t.scopeLabels[result.scope]}</strong>
                  <div className="result-price"><small>{t.budget}</small><b>{result.customQuote ? t.customBudget : result.min.toLocaleString('fr-FR') + ' € à ' + result.max.toLocaleString('fr-FR') + ' €'}</b></div>
                  <div className="result-delay"><small>{t.timing}</small><b>{result.delay}</b></div>
                </article>
                <article><span>{t.included}</span><ul>{result.included.map((item) => <li key={item}>{item}</li>)}</ul></article>
                <article className="result-later"><span>{t.later}</span>{result.later.map((item) => <p key={item}>{item}</p>)}</article>
              </div>

              <p className="estimator-note">{t.note} {result.customQuote ? t.complexReason : result.scope === 'complete' ? t.completeReason : t.starterReason}</p>
              <div className="result-actions">
                <button type="button" className="v2-primary" onClick={contact}>{t.contact} →</button>
                <button type="button" className="v2-secondary" onClick={copyResult}>{copied ? t.copied : t.copySummary}</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
